import { useAuth } from "@/hooks/use-auth";
import { useClient, useQuery } from "urql";

import { GetOnCourseDeviceAppDataQuery } from "../documents/device-data";
import type { ResultOf } from "../graphql";
import { getDeviceDataQueryOptions } from "./device-data-query-options";

type DeviceData = ResultOf<typeof GetOnCourseDeviceAppDataQuery>;

type UseDeviceDataResult = {
  data: DeviceData | null;
  loading: boolean;
  error: Error | null;
  refetch: () => void;
  refetchAndWait: () => Promise<DeviceData | null>;
};

const normalizeDeviceData = (data: DeviceData): DeviceData => {
  const appData = data.applicationData;
  if (appData?.__typename !== "OnCourseKioskDeviceData") {
    return data;
  }

  const device = appData.device;
  const hole = device?.hole;
  const tees = hole?.tees;
  if (!device || !hole || !tees) {
    return data;
  }

  return {
    ...data,
    applicationData: {
      ...appData,
      device: {
        ...device,
        hole: {
          ...hole,
          tees: tees.map((tee) => ({
            ...tee,
            distance: Math.round(tee.distance),
          })),
        },
      },
    },
  };
};

export const useDeviceData = (): UseDeviceDataResult => {
  const { isAuthenticated } = useAuth();
  const client = useClient();
  const [{ data, fetching, error }, reexecute] = useQuery(
    getDeviceDataQueryOptions(isAuthenticated)
  );

  const refetch = () => {
    reexecute({ requestPolicy: "network-only" });
  };

  const refetchAndWait = async (): Promise<DeviceData | null> => {
    const result = await client
      .query(
        GetOnCourseDeviceAppDataQuery,
        {},
        { requestPolicy: "network-only" }
      )
      .toPromise();
    return result.data ? normalizeDeviceData(result.data) : null;
  };

  return {
    data: data ? normalizeDeviceData(data) : null,
    loading: fetching,
    error: error ?? null,
    refetch,
    refetchAndWait,
  };
};
